import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { permissionApi } from '@/services/permissionsRolesApi';
import { usePaginatedList, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Eye, Edit, Trash2, Shield, Search, BarChart3, Database, CheckCircle2, XCircle } from 'lucide-react';
import { LoadingState, EmptyState } from '@/components/common';
import type { Permission, PermissionAction, ListPermissionsParams } from '@/types/permissionsRoles';

export const PermissionListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterResource, setFilterResource] = useState<string>('all');
  const [filterAction, setFilterAction] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [initializeModalOpen, setInitializeModalOpen] = useState(false);
  const [availableResources, setAvailableResources] = useState<string[]>([]);
  const { hasPermission } = useAdminAuth();

  const canRead = hasPermission('READ_PERMISSION', 'permission');
  const canCreate = hasPermission('CREATE_PERMISSION', 'permission');
  const canEdit = hasPermission('UPDATE_PERMISSION', 'permission');
  const canDelete = hasPermission('DELETE_PERMISSION', 'permission');
  const canInitialize = hasPermission('SYSTEM_ADMIN', 'permission');

  // Build fetch function with filters
  const fetchPermissionsFn = useMemo(() => {
    return async (params: { page: number; size: number }) => {
      const listParams: ListPermissionsParams = {
        ...params,
        sort: `${sortBy},${sortDirection}`,
        active: filterStatus !== 'all' ? filterStatus === 'active' : undefined,
        systemPermission: filterType !== 'all' ? filterType === 'system' : undefined,
        resource: filterResource !== 'all' ? filterResource : undefined,
        action: filterAction !== 'all' ? (filterAction as PermissionAction) : undefined,
        search: searchQuery.trim() || undefined,
      };
      return permissionApi.list(listParams);
    };
  }, [sortBy, sortDirection, filterStatus, filterType, filterResource, filterAction, searchQuery]);

  // Use paginated list hook
  const {
    data: permissions,
    isLoading,
    error,
    pagination,
    setPage,
    setSize,
    refetch,
  } = usePaginatedList<Permission>({
    fetchFn: fetchPermissionsFn,
    entityName: 'permissions',
    enabled: canRead,
    suppress404Errors: true,
  });

  // Extract unique resources from permissions
  useEffect(() => {
    const resources = Array.from(new Set(permissions.map((p) => p.resource).filter(Boolean) as string[]));
    setAvailableResources(resources.sort());
  }, [permissions]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (permissionId: string) => {
      await permissionApi.delete(permissionId);
    },
    entityName: 'permission',
    operation: 'delete',
    onSuccess: () => {
      refetch();
    },
  });

  // Toggle status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: async ({ permissionId, active }: { permissionId: string; active: boolean }) => {
      await permissionApi.toggleStatus(permissionId, active);
    },
    entityName: 'permission',
    operation: 'update',
    successMessage: (_data, variables) => `Permission ${variables.active ? 'activated' : 'deactivated'} successfully`,
    onSuccess: () => {
      refetch();
    },
  });

  // Initialize mutation
  const initializeMutation = useMutation({
    mutationFn: async () => permissionApi.initialize(),
    entityName: 'permissions',
    operation: 'custom',
    successMessage: (result) => `Initialized ${result.created} permission(s), ${result.skipped} already existed`,
    onSuccess: () => {
      setInitializeModalOpen(false);
      refetch();
    },
  });

  const handleSearch = () => {
    setPage(0);
    refetch();
  };

  const handleDelete = async (permissionId: string, permissionName: string) => {
    if (!window.confirm(`Are you sure you want to delete permission "${permissionName}"?`)) return;
    await deleteMutation.mutate(permissionId);
  };

  const handleToggleStatus = async (permissionId: string, currentActive: boolean) => {
    await toggleStatusMutation.mutate({ permissionId, active: !currentActive });
  };

  const handleInitialize = async () => {
    await initializeMutation.mutateAsync();
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  const getStatusColor = (active: boolean) => {
    return active
      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
  };

  const ACTIONS: PermissionAction[] = ['CREATE', 'READ', 'UPDATE', 'DELETE', 'MANAGE'];

  if (!canRead) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">You do not have permission to view permissions.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Permissions</h1>
          <p className="text-muted-foreground mt-2">Manage system and custom permissions</p>
        </div>
        <div className="flex gap-2">
          {canCreate && (
            <Button asChild>
              <Link to="/admin/permissions/new">
                <Plus className="mr-2 h-4 w-4" />
                Create Permission
              </Link>
            </Button>
          )}
          {canInitialize && (
            <Button variant="outline" onClick={() => setInitializeModalOpen(true)}>
              <Database className="mr-2 h-4 w-4" />
              Initialize
            </Button>
          )}
          <Button asChild variant="outline">
            <Link to="/admin/permissions/statistics">
              <BarChart3 className="mr-2 h-4 w-4" />
              Statistics
            </Link>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters & Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-5">
            <div className="md:col-span-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Search permissions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="system">System</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterResource} onValueChange={setFilterResource}>
              <SelectTrigger>
                <SelectValue placeholder="All Resources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Resources</SelectItem>
                {availableResources.map((resource) => (
                  <SelectItem key={resource} value={resource}>
                    {resource}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-4">
            <Select value={filterAction} onValueChange={setFilterAction}>
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {ACTIONS.map((action) => (
                  <SelectItem key={action} value={action}>
                    {action}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Permissions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Permissions ({pagination.totalElements})</CardTitle>
          <CardDescription>List of all permissions</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState message="Loading permissions..." />
          ) : (error || permissions.length === 0) ? (
            <EmptyState
              title="No data found"
              description={error ? "Unable to load permissions. Please try again or create a new one." : "Get started by creating your first permission."}
              icon={Shield}
              actions={[
                ...(canCreate ? [{
                  label: 'Create a new one',
                  onClick: () => navigate('/admin/permissions/new'),
                  variant: 'default' as const,
                }] : []),
                {
                  label: 'Try again',
                  onClick: () => refetch(),
                  variant: 'outline' as const,
                },
              ]}
              searchQuery={searchQuery.trim() || undefined}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('name')}
                    >
                      Name
                      {sortBy === 'name' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('resource')}
                    >
                      Resource
                      {sortBy === 'resource' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('action')}
                    >
                      Action
                      {sortBy === 'action' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('active')}
                    >
                      Status
                      {sortBy === 'active' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {permissions.map((permission) => (
                    <TableRow key={permission.id}>
                      <TableCell className="font-medium">{permission.name}</TableCell>
                      <TableCell className="max-w-md truncate">
                        {permission.description || '—'}
                      </TableCell>
                      <TableCell>{permission.resource || '—'}</TableCell>
                      <TableCell>{permission.action || '—'}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(permission.active)}`}>
                          {permission.active ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {permission.systemPermission ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            System
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                            Custom
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/admin/permissions/${permission.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" asChild>
                              <Link to={`/admin/permissions/${permission.id}/edit`}>
                                <Edit className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleToggleStatus(permission.id, permission.active)}
                              title={permission.active ? 'Deactivate' : 'Activate'}
                            >
                              {permission.active ? (
                                <XCircle className="h-4 w-4 text-yellow-600" />
                              ) : (
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                              )}
                            </Button>
                          )}
                          {canDelete && !permission.systemPermission && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(permission.id, permission.name)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {(pagination.totalPages > 1 || pagination.totalElements > pagination.size) && (
                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-muted-foreground">
                      Page {pagination.page + 1} of {pagination.totalPages || 1} ({pagination.totalElements} total)
                    </div>
                    <Select value={pagination.size.toString()} onValueChange={(v) => setSize(parseInt(v))}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.max(0, pagination.page - 1))}
                      disabled={pagination.page === 0 || isLoading}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.min((pagination.totalPages || 1) - 1, pagination.page + 1))}
                      disabled={pagination.page >= (pagination.totalPages || 1) - 1 || isLoading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Initialize Modal */}
      <Dialog open={initializeModalOpen} onOpenChange={setInitializeModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Initialize System Permissions</DialogTitle>
            <DialogDescription>
              This will create 18 system permissions if they don't already exist. This operation is idempotent and safe to run multiple times.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {initializeMutation.isLoading ? (
              <LoadingState message="Initializing permissions..." />
            ) : (
              <p className="text-sm text-muted-foreground">
                Click Confirm to proceed with initialization.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInitializeModalOpen(false)} disabled={initializeMutation.isLoading}>
              Cancel
            </Button>
            <Button onClick={handleInitialize} disabled={initializeMutation.isLoading}>
              {initializeMutation.isLoading ? 'Initializing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PermissionListPage;
